import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Send, Clock, Check,
  ChevronDown, ExternalLink, Trash2, Calendar, X
} from "lucide-react";
import api from "../lib/api";

const PLATFORMS_META = {
  youtube: { name: "YouTube", icon: "▶", color: "bg-red-600", ring: "ring-red-500", selected: "bg-red-500/15 border-red-400 text-red-300" },
  tiktok: { name: "TikTok", icon: "♪", color: "bg-gray-900", ring: "ring-gray-700", selected: "bg-teal-500/15 border-teal-400 text-teal-300" },
  instagram: { name: "Instagram", icon: "📷", color: "bg-gradient-to-r from-purple-600 to-pink-500", ring: "ring-pink-500", selected: "bg-pink-500/15 border-pink-400 text-pink-300" },
};

const CONTENT_TYPES = [
  { id: "text", label: "Text Post" },
  { id: "image", label: "Image" },
  { id: "short", label: "Short Video" },
  { id: "reel", label: "Reel" },
  { id: "story", label: "Story" },
];

const TONES = [
  { id: "casual", label: "Casual" },
  { id: "professional", label: "Professional" },
  { id: "funny", label: "Funny" },
  { id: "informative", label: "Informative" },
];

export default function PublishPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState({});
  const [assistants, setAssistants] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [contentType, setContentType] = useState("text");
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // AI generator
  const [showAI, setShowAI] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState("casual");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContent, setAiContent] = useState(null);

  useEffect(() => {
    const preselectedAI = searchParams.get("ai");
    if (preselectedAI) setSelectedAssistant(preselectedAI);
    Promise.all([
      api.get("/social-accounts/status").then((r) => setStatus(r.data)),
      api.get("/assistants").then((r) => setAssistants(r.data.assistants || r.data || [])).catch(() => {}),
      api.get("/cross-post/history?limit=10").then((r) => setHistory(r.data.posts || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [searchParams]);

  const togglePlatform = (p) => {
    if (!status[p]?.connected) return;
    setSelectedPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  const handlePublish = async () => {
    if (!content.trim() || selectedPlatforms.length === 0) return;
    setPublishing(true);
    setPublished(false);
    try {
      const r = await api.post("/cross-post", {
        content_text: content.trim(),
        platforms: selectedPlatforms,
        content_type: contentType,
        ai_assistant_id: selectedAssistant,
      });
      setPublishResult(r.data);
      setPublished(true);
      toast.success("Published to " + selectedPlatforms.map((p) => PLATFORMS_META[p].name).join(", ") + "!");
      setHistory((prev) => [{ id: r.data.id, platforms: selectedPlatforms, content_text: content.trim(), status: "posted", platform_results: r.data.platform_results, created_at: new Date().toISOString() }, ...prev]);
      // Update status post counts
      setStatus((prev) => {
        const updated = { ...prev };
        selectedPlatforms.forEach((p) => {
          if (updated[p]) updated[p] = { ...updated[p], post_count: (updated[p].post_count || 0) + 1 };
        });
        return updated;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) return;
    setAiGenerating(true);
    try {
      const r = await api.post("/ai-studio/social-content", { topic: aiTopic.trim(), tone: aiTone });
      setAiContent(r.data);
    } catch {
      toast.error("AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAIContent = (platform) => {
    if (!aiContent) return;
    const pc = aiContent[platform];
    if (!pc) return;
    if (platform === "youtube") setContent(pc.title + "\n\n" + pc.description + "\n\nTags: " + (pc.tags || []).join(", "));
    else setContent((pc.caption || "") + "\n\n" + (pc.hashtags || []).map((h) => `#${h}`).join(" "));
    setShowAI(false);
  };

  const applyAllAIContent = () => {
    if (!aiContent) return;
    const parts = [];
    if (aiContent.youtube) parts.push(`📺 YouTube:\n${aiContent.youtube.title}\n${aiContent.youtube.description}`);
    if (aiContent.tiktok) parts.push(`🎵 TikTok:\n${aiContent.tiktok.caption}\n${(aiContent.tiktok.hashtags || []).map((h) => `#${h}`).join(" ")}`);
    if (aiContent.instagram) parts.push(`📸 Instagram:\n${aiContent.instagram.caption}\n${(aiContent.instagram.hashtags || []).map((h) => `#${h}`).join(" ")}`);
    setContent(parts.join("\n\n---\n\n"));
    setShowAI(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen" data-testid="publish-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <Send className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-sm font-semibold text-foreground">Social Publishing</h1>
        <div className="flex-1" />
        <Link to="/settings/social-accounts" className="text-xs text-cyan-400 no-underline hover:text-cyan-300" data-testid="manage-accounts-link">Manage Accounts</Link>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Content Creator */}
        <div className="bg-card rounded-2xl border border-border/60 shadow-sm overflow-hidden" data-testid="content-creator">
          <div className="bg-gradient-to-r from-cyan-600/25 via-violet-600/25 to-fuchsia-600/25 border-b border-white/10 px-5 py-4">
            <h2 className="font-outfit text-base font-semibold text-white">Create & Publish</h2>
            <p className="text-xs text-white/70 mt-0.5">Share content across YouTube, TikTok, and Instagram</p>
          </div>

          <div className="p-5 space-y-5">
            {/* Platform Selector */}
            <div>
              <label className="block text-xs font-medium text-foreground/80 mb-2">Platforms</label>
              <div className="flex gap-3" data-testid="platform-selector">
                {Object.entries(PLATFORMS_META).map(([key, p]) => {
                  const connected = status[key]?.connected;
                  const active = selectedPlatforms.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => togglePlatform(key)}
                      disabled={!connected}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer ${
                        active ? p.selected + " shadow-sm" : connected ? "border-border bg-card text-muted-foreground hover:border-border" : "border-border/60 bg-muted/40 text-muted-foreground/60 cursor-not-allowed"
                      }`}
                      data-testid={`platform-toggle-${key}`}
                    >
                      <span className="text-lg">{p.icon}</span>
                      <span>{p.name}</span>
                      {!connected && <Link to="/settings/social-accounts" onClick={(e) => e.stopPropagation()} className="text-[10px] text-cyan-400 no-underline ml-1">Connect</Link>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Type */}
            <div className="flex gap-2 flex-wrap">
              {CONTENT_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  onClick={() => setContentType(ct.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${contentType === ct.id ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300" : "bg-card border-border text-muted-foreground hover:border-border"}`}
                  data-testid={`type-${ct.id}`}
                >
                  {ct.label}
                </button>
              ))}
            </div>

            {/* Post As */}
            <div>
              <label className="block text-xs font-medium text-foreground/80 mb-2">Post as</label>
              <select
                value={selectedAssistant}
                onChange={(e) => setSelectedAssistant(e.target.value)}
                className="w-full max-w-xs px-3 py-2 bg-muted/40 border border-border rounded-lg text-sm text-foreground/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400/25"
                data-testid="post-as-selector"
              >
                <option value="">You (personal)</option>
                {assistants.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.purpose?.replace(/_/g, " ")})</option>)}
              </select>
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-foreground/80">Content</label>
                <button onClick={() => setShowAI(true)} className="flex items-center gap-1 text-xs font-medium text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/15 px-3 py-1.5 rounded-lg border-0 cursor-pointer transition-colors" data-testid="ai-write-btn">
                  <Sparkles className="w-3.5 h-3.5" /> AI Write For Me
                </button>
              </div>
              <textarea
                data-testid="publish-content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What do you want to share with the world?"
                rows={5}
                className="w-full px-4 py-3 bg-muted/40 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none"
              />
              <p className="text-[10px] text-muted-foreground/70 mt-1 text-right">{content.length} characters</p>
            </div>

            {/* Publish Button */}
            <Button
              onClick={handlePublish}
              disabled={publishing || !content.trim() || selectedPlatforms.length === 0}
              className="w-full bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 text-white h-12 text-base font-semibold rounded-xl shadow-glow-sm hover:-translate-y-0.5 transition-all press-down disabled:opacity-40"
              data-testid="publish-btn"
            >
              {publishing ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Publishing...</> : <><Send className="w-5 h-5 mr-2" /> Publish to {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? "s" : ""}</>}
            </Button>
          </div>
        </div>

        {/* Success Result */}
        {published && publishResult && (
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-2xl p-5 animate-fade-in-up" data-testid="publish-success">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center"><Check className="w-5 h-5 text-emerald-400" /></div>
              <div>
                <p className="text-sm font-semibold text-emerald-300">Published Successfully!</p>
                <p className="text-xs text-emerald-400">Your content is now live on {publishResult.platforms.length} platform{publishResult.platforms.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="space-y-2">
              {(publishResult.platform_results || []).map((pr, i) => (
                <div key={i} className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span>{PLATFORMS_META[pr.platform]?.icon}</span>
                    <span className="text-sm font-medium text-foreground">{PLATFORMS_META[pr.platform]?.name}</span>
                    <span className="text-xs text-emerald-400 font-medium">{pr.status}</span>
                  </div>
                  {pr.post_url && (
                    <a href={pr.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 no-underline flex items-center gap-1 hover:text-cyan-300">
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Platform Previews */}
        {content.trim() && selectedPlatforms.length > 0 && !published && (
          <div data-testid="platform-previews">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold mb-3">Preview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {selectedPlatforms.includes("youtube") && (
                <div className="bg-card rounded-xl border border-border/60 overflow-hidden" data-testid="preview-youtube">
                  <div className="bg-gray-900 h-28 flex items-center justify-center"><span className="text-3xl">▶</span></div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-foreground line-clamp-2">{content.split("\n")[0]}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{content}</p>
                    <span className="text-[10px] text-red-400 font-medium mt-2 block">YouTube Community Post</span>
                  </div>
                </div>
              )}
              {selectedPlatforms.includes("tiktok") && (
                <div className="bg-gray-900 rounded-xl overflow-hidden" data-testid="preview-tiktok">
                  <div className="h-28 flex items-center justify-center"><span className="text-3xl">♪</span></div>
                  <div className="p-3">
                    <p className="text-sm text-white line-clamp-3">{content}</p>
                    <span className="text-[10px] text-teal-400 font-medium mt-2 block">#fyp #viral</span>
                  </div>
                </div>
              )}
              {selectedPlatforms.includes("instagram") && (
                <div className="bg-card rounded-xl border border-border/60 overflow-hidden" data-testid="preview-instagram">
                  <div className="bg-gradient-to-br from-purple-500/15 to-pink-500/15 h-28 flex items-center justify-center"><span className="text-3xl">📸</span></div>
                  <div className="p-3">
                    <p className="text-sm text-foreground line-clamp-3">{content}</p>
                    <span className="text-[10px] text-pink-400 font-medium mt-2 block">Instagram Post</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Publishing History */}
        {history.length > 0 && (
          <div data-testid="publish-history">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold mb-3">Recent Activity</h3>
            <div className="bg-card rounded-2xl border border-border/60 divide-y divide-border/50 overflow-hidden">
              {history.map((h) => (
                <div key={h.id} className="px-4 py-3 flex items-center gap-3" data-testid={`history-${h.id}`}>
                  <div className="flex gap-1 flex-shrink-0">
                    {(h.platforms || []).map((p) => (
                      <span key={p} className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] text-white ${PLATFORMS_META[p]?.color || "bg-muted-foreground/40"}`}>{PLATFORMS_META[p]?.icon}</span>
                    ))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{h.content_text}</p>
                    <p className="text-[10px] text-muted-foreground/70">{new Date(h.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${h.status === "posted" ? "bg-emerald-500/10 text-emerald-400" : h.status === "scheduled" ? "bg-amber-500/10 text-amber-400" : "bg-red-500/10 text-red-400"}`}>
                    {h.status === "posted" ? "Posted" : h.status === "scheduled" ? "Scheduled" : "Failed"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Content Generator Modal */}
      {showAI && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAI(false)} data-testid="ai-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-cyan-600/30 to-violet-600/30 border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-outfit text-base font-semibold text-white">AI Content Generator</h2>
                <p className="text-xs text-white/70">Generate platform-optimized content</p>
              </div>
              <button onClick={() => setShowAI(false)} className="text-white/60 hover:text-white bg-transparent border-0 cursor-pointer"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Topic</label>
                <input
                  data-testid="ai-topic-input"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="What should the AI write about?"
                  className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Tone</label>
                <div className="flex gap-2">
                  {TONES.map((t) => (
                    <button key={t.id} onClick={() => setAiTone(t.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${aiTone === t.id ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300" : "bg-card border-border text-muted-foreground"}`} data-testid={`tone-${t.id}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                onClick={handleAIGenerate}
                disabled={aiGenerating || !aiTopic.trim()}
                className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-10 rounded-lg press-down disabled:opacity-40"
                data-testid="ai-generate-btn"
              >
                {aiGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
              </Button>

              {aiContent && (
                <div className="space-y-3 pt-2" data-testid="ai-content-results">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground/80">Generated Content</span>
                    <button onClick={applyAllAIContent} className="text-xs text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/15 px-3 py-1 rounded-lg border-0 cursor-pointer font-medium" data-testid="use-all-btn">Use All</button>
                  </div>

                  {aiContent.youtube && (
                    <div className="border border-red-500/20 rounded-xl p-3 bg-red-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-red-400 flex items-center gap-1">▶ YouTube</span>
                        <button onClick={() => applyAIContent("youtube")} className="text-[10px] text-red-400 bg-red-500/15 hover:bg-red-500/25 px-2 py-0.5 rounded border-0 cursor-pointer" data-testid="use-youtube">Use</button>
                      </div>
                      <p className="text-sm font-medium text-foreground">{aiContent.youtube.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{aiContent.youtube.description}</p>
                    </div>
                  )}
                  {aiContent.tiktok && (
                    <div className="border border-teal-500/20 rounded-xl p-3 bg-teal-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-teal-400 flex items-center gap-1">♪ TikTok</span>
                        <button onClick={() => applyAIContent("tiktok")} className="text-[10px] text-teal-400 bg-teal-500/15 hover:bg-teal-500/25 px-2 py-0.5 rounded border-0 cursor-pointer" data-testid="use-tiktok">Use</button>
                      </div>
                      <p className="text-sm text-foreground">{aiContent.tiktok.caption}</p>
                      <p className="text-xs text-teal-400 mt-1">{(aiContent.tiktok.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
                    </div>
                  )}
                  {aiContent.instagram && (
                    <div className="border border-pink-500/20 rounded-xl p-3 bg-pink-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-pink-400 flex items-center gap-1">📸 Instagram</span>
                        <button onClick={() => applyAIContent("instagram")} className="text-[10px] text-pink-400 bg-pink-500/15 hover:bg-pink-500/25 px-2 py-0.5 rounded border-0 cursor-pointer" data-testid="use-instagram">Use</button>
                      </div>
                      <p className="text-sm text-foreground">{aiContent.instagram.caption}</p>
                      <p className="text-xs text-pink-400 mt-1">{(aiContent.instagram.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
